import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast.ts";

export interface Group {
  id: string;
  name: string;
  is_billable_group?: boolean;
  countsByRole?: Record<string, number>;
}

export const useGroups = (includeCount: boolean = false) => {
  return useQuery<Group[]>({
    queryKey: ["groups", includeCount],
    queryFn: async () => {
      const { data } = await fetchApi<{ groups: Group[] }>({
        url: "/v2/groups",
        method: "GET",
        params: { includeCount: includeCount.toString() },
      });
      return data.groups;
    },
  });
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<Group, Error, { name: string }>({
    mutationFn: async (data) => {
      const { data: response } = await fetchApi<{ group: Group }>({
        url: "/v2/groups",
        method: "POST",
        data,
      });
      return response.group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      toast({ title: "Group created successfully" });
    },
    onError: () => {
      toast({ title: "Failed to create group", variant: "destructive" });
    },
  });
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    Group,
    Error,
    { id: string; name: string; is_billable_group?: boolean }
  >({
    mutationFn: async ({ id, name, is_billable_group }) => {
      const { data: response } = await fetchApi<Group>({
        url: `/v2/groups/${id}`,
        method: "PUT",
        data: { name, is_billable_group },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      toast({ title: "Group updated successfully" });
    },
    onError: () => {
      toast({ title: "Failed to update group", variant: "destructive" });
    },
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await fetchApi({
        url: `/v2/groups/${id}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      toast({ title: "Group deleted successfully" });
    },
    onError: () => {
      toast({ title: "Failed to delete group", variant: "destructive" });
    },
  });
};
